import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Combine, Package, DollarSign, ArrowRight, Download, Layers } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import InfoTip from '@/components/InfoTip';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';

const ConsolidateOrders = () => {
  const { format } = useCurrency();
  const clusters = [
    { group: 'MLCC 0.1uF 50V X7R', mpns: ['GRM188R71H104KA93D', 'CL10B104KA8NNNC', 'CC0603KRX7R9BB104'], boms: ['Main Board v2', 'Power Supply v1'], totalQty: 450000, currentSpend: 12600, consolidatedSpend: 9450, savings: 3150, type: 'Same Part' },
    { group: '10K Resistor 0402', mpns: ['RC0402FR-0710KL', 'CRCW040210K0FKED'], boms: ['Main Board v2', 'IoT Sensor'], totalQty: 800000, currentSpend: 4800, consolidatedSpend: 3840, savings: 960, type: 'FFF Equivalent' },
    { group: 'LED 0603 Green', mpns: ['LTST-C190GKT', 'APT1608SGC'], boms: ['Display Board', 'Main Board v2'], totalQty: 150000, currentSpend: 3000, consolidatedSpend: 2320, savings: 680, type: 'FFF Equivalent' },
  ];
  const totalSavings = clusters.reduce((s, c) => s + c.savings, 0);

  return (
    <Layout title="Consolidate Orders" subtitle="Combine same or FFF-equivalent parts across BOMs for volume pricing">
      <div className="space-y-6" data-testid="consolidate-orders-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-green-50 border-green-200 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-green-600 flex items-center">Total Savings <InfoTip text="Savings from consolidating identical or FFF parts" /></p><p className="text-2xl font-bold text-green-700">{format(totalSavings)}</p></CardContent></Card>
          <Card className="bg-white border-gray-200 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-gray-500">Consolidation Groups</p><p className="text-2xl font-bold text-gray-900">{clusters.length}</p></CardContent></Card>
          <Card className="bg-white border-gray-200 shadow-sm"><CardContent className="pt-6"><p className="text-sm text-gray-500">BOMs Affected</p><p className="text-2xl font-bold text-gray-900">4</p></CardContent></Card>
        </div>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader><CardTitle className="flex items-center gap-2"><Combine className="h-5 w-5 text-purple-600" /> Consolidation Opportunities</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {clusters.map((c, idx) => (
                <div key={idx} className="p-4 border border-gray-200 rounded-lg" data-testid={`cluster-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-purple-500" />
                      <h4 className="font-bold text-gray-900">{c.group}</h4>
                      <Badge className={c.type === 'Same Part' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>{c.type}</Badge>
                    </div>
                    <Badge className="bg-green-100 text-green-800 text-lg px-3">Save {format(c.savings)}</Badge>
                  </div>
                  <div className="grid grid-cols-5 gap-4 text-sm mb-2">
                    <div><p className="text-xs text-gray-500">MPNs</p><p className="font-medium">{c.mpns.length} parts</p></div>
                    <div><p className="text-xs text-gray-500">Across BOMs</p><p className="font-medium">{c.boms.join(', ')}</p></div>
                    <div><p className="text-xs text-gray-500">Total Qty</p><p className="font-medium">{c.totalQty.toLocaleString()}</p></div>
                    <div><p className="text-xs text-gray-500">Current Spend</p><p className="font-medium">{format(c.currentSpend)}</p></div>
                    <div><p className="text-xs text-gray-500">Consolidated</p><p className="font-bold text-green-700">{format(c.consolidatedSpend)}</p></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid={`consolidate-btn-${idx}`}
                      onClick={() => { toast.success(`Consolidation initiated for "${c.group}" — ${c.mpns.length} parts across ${c.boms.length} BOMs`); }}>
                      Consolidate
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`compare-btn-${idx}`}
                      onClick={() => { toast.info(`Opening datasheet comparison for ${c.mpns.join(', ')}`); }}>
                      Compare Datasheets
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
export default ConsolidateOrders;
